import { useEffect, useState, type ChangeEvent } from 'react';
import type { Tenant } from '../../types/lac';
import { useI18n } from '../../i18n/useI18n';
import {
  TENANT_LOGO_MAX_BYTES,
  logoToFormValue,
  readFileAsDataUrl,
  toPhotoDisplaySrc,
  toPhotoPayload,
} from '../../utils/imageUtils';

function isLogoHydratingFromTenant(
  tenant: Tenant,
  logo: string | null,
): boolean {
  return (
    logo == null &&
    typeof Blob !== 'undefined' &&
    tenant.logo instanceof Blob &&
    tenant.logo.size > 0
  );
}

export function useLacTenantDetailsLogo(tenant: Tenant) {
  const { translate } = useI18n();
  const [logo, setLogo] = useState<string | null>(() =>
    logoToFormValue(tenant.logo),
  );
  const [logoTouched, setLogoTouched] = useState(false);
  const [isLogoHydrating, setIsLogoHydrating] = useState(() =>
    isLogoHydratingFromTenant(tenant, logoToFormValue(tenant.logo)),
  );
  const [logoError, setLogoError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const hydrateLogoFromBlob = async () => {
      if (logo != null) {
        setIsLogoHydrating(false);
        return;
      }
      if (typeof Blob === 'undefined' || !(tenant.logo instanceof Blob)) {
        setIsLogoHydrating(false);
        return;
      }
      if (tenant.logo.size === 0) {
        setIsLogoHydrating(false);
        return;
      }

      try {
        const dataUrl = await readFileAsDataUrl(
          new File([tenant.logo], 'logo', {
            type: tenant.logo.type || 'image/png',
          }),
        );
        if (!cancelled) {
          setLogo(toPhotoPayload(dataUrl));
          setIsLogoHydrating(false);
        }
      } catch {
        if (!cancelled) {
          setIsLogoHydrating(false);
        }
      }
    };

    void hydrateLogoFromBlob();

    return () => {
      cancelled = true;
    };
  }, [logo, tenant.logo]);

  const handleLogoChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setLogoError(translate('lac.organisations.details.logoInvalidType'));
      return;
    }

    if (file.size > TENANT_LOGO_MAX_BYTES) {
      setLogoError(translate('lac.organisations.details.logoTooLarge'));
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setLogo(toPhotoPayload(dataUrl));
      setLogoTouched(true);
      setLogoError(null);
    } catch {
      setLogoError(translate('lac.organisations.details.logoReadError'));
    }
  };

  const handleRemoveLogo = () => {
    setLogo(null);
    setLogoTouched(true);
    setLogoError(null);
  };

  const resetLogo = () => {
    setLogo(logoToFormValue(tenant.logo));
    setLogoTouched(false);
    setLogoError(null);
    setIsLogoHydrating(
      typeof Blob !== 'undefined' &&
        tenant.logo instanceof Blob &&
        tenant.logo.size > 0 &&
        logoToFormValue(tenant.logo) == null,
    );
  };

  return {
    logo,
    logoTouched,
    isLogoHydrating,
    logoError,
    logoDisplaySrc: toPhotoDisplaySrc(logo),
    handleLogoChange,
    handleRemoveLogo,
    resetLogo,
  };
}
